import { useQuery } from '@tanstack/react-query';
import http from 'helpers/requests';

/**
 * Hook to fetch trial status for current user
 *
 * GET /api/onboarding/trial-status
 *
 * The endpoint answers with one of three shapes:
 *
 *   on a trial plan:  { hasActiveTrial: true, isExpired, daysRemaining,
 *                       trialStartDate, trialEndDate, organizationType, planName }
 *   on a paid plan:   { hasActiveTrial: false, hasActiveSubscription: true,
 *                       organizationType, planName }          // NO dates
 *   with no plan:     { hasActiveTrial: false, isExpired: true, daysRemaining: 0 }
 *
 * Dates are ISO strings, not Date objects, and `trialEndDate` is null for an
 * unlimited trial. Note the paid shape carries no expiry date at all — for a
 * plan's start/expiry regardless of type, use `usePlanSummary` (GET
 * /plan/summary) instead of this hook.
 *
 * @param {Object} options - Query options
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @param {number} options.staleTime - Time in ms before data is considered stale (default: 5 minutes)
 * @param {number} options.gcTime - Time in ms to keep data in cache (default: 10 minutes)
 * @param {number} options.refetchInterval - Interval in ms to refetch data (default: disabled)
 *
 * @returns {Object} Query object with data, isLoading, error, refetch
 *
 * @example
 * const { data, isLoading } = useTrialStatus();
 *
 * if (data?.hasActiveTrial && data.daysRemaining <= 3) {
 *   // Show upgrade prompt
 * }
 */
const useTrialStatus = (options = {}) => {
  const {
    enabled = true,
    staleTime = 300000, // 5 minutes - trial status doesn't change frequently
    gcTime = 600000, // 10 minutes
    refetchInterval = undefined,
  } = options;

  return useQuery({
    queryKey: ['trialStatus'],
    queryFn: async () => {
      const response = await http.get('/onboarding/trial-status');
      return response.data;
    },
    enabled,
    staleTime,
    gcTime,
    refetchInterval,
    retry: 2, // Retry twice on failure
  });
};

export default useTrialStatus;
